/**
 * Full-screen loading state — shown during lazy-load Suspense and auth restore.
 *
 * After `timeoutMs` (default 10 s), shows a recovery overlay so the user is
 * never permanently stuck on the purple spinner.
 *
 * FAIL SOFT (changed June 12, 2026): a timeout NEVER auto-wipes auth state —
 * a slow network is not a corrupted token, and the old auto-wipe destroyed
 * valid sessions (managed children, with no password and single-use login
 * links, were locked out entirely). The recovery overlay keeps two manual
 * escape hatches: "Reload app" (plain reload — session preserved; recovers
 * the slow-network case) and "Sign out and start over" (explicit
 * clearAndRedirect — the wipe for the genuine D-025 stale-lock case, now a
 * user decision instead of an automatic one).
 *
 * `clearOnTimeout` is retained for call-site compatibility but no longer
 * wipes — both paths show the recovery overlay.
 *
 * Framer Motion pulse respects prefers-reduced-motion (Rule 15).
 */
import { useState, useEffect }  from 'react'
import { motion }               from 'framer-motion'
import { useReducedMotion }     from '@/hooks/useReducedMotion'
import { clearAndRedirect }     from '@/lib/authCleanup'
import { Spinner }              from '@/components/Spinner'

interface LoadingScreenProps {
  /** After this many ms show recovery options. Default 10 000. Pass 0 to disable. */
  timeoutMs?:      number
  onRetry?:        () => void
  /**
   * DEPRECATED June 12, 2026 — no longer auto-wipes (see header comment).
   * Retained so existing call sites compile; both values show the recovery
   * overlay on timeout. The wipe is now only on the explicit
   * "Sign out and start over" button.
   */
  clearOnTimeout?: boolean
}

export function LoadingScreen({ timeoutMs = 10_000, onRetry, clearOnTimeout = false }: LoadingScreenProps) {
  const reducedMotion  = useReducedMotion()
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (!timeoutMs) return
    const t = setTimeout(() => {
      // FAIL SOFT — never auto-wipe on a timeout (see header comment).
      console.warn(
        `[Nudge/auth] LoadingScreen timeout — showing recovery UI, session preserved${clearOnTimeout ? ' (auth context)' : ''}`
      )
      setTimedOut(true)
    }, timeoutMs)
    return () => clearTimeout(t)
  }, [timeoutMs, clearOnTimeout])

  return (
    <div
      className="min-h-screen bg-page flex items-center justify-center p-6"
      role="status"
      aria-label={timedOut ? 'Loading taking too long' : 'Loading'}
      aria-busy={!timedOut}
    >
      <div className="flex flex-col items-center gap-6 w-full max-w-sm text-center">
        {/* Spinner — shared <Spinner /> component, text-primary (D-025) */}
        <motion.div
          animate={reducedMotion || timedOut ? {} : { opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Spinner size="lg" className={timedOut ? 'border-text-tertiary border-t-transparent' : ''} />
        </motion.div>

        {/* Recovery UI — shown after timeout */}
        {timedOut && (
          <div className="flex flex-col items-center gap-4 w-full">
            <div>
              <p className="font-semibold text-text-primary mb-1">Taking longer than expected</p>
              <p className="text-sm text-text-muted">
                This can happen after inactivity or a network hiccup.
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="btn-primary w-full"
                >
                  Try again
                </button>
              )}
              <button
                onClick={() => window.location.reload()}
                className="btn-ghost w-full"
              >
                Reload app
              </button>
              <button
                onClick={() => clearAndRedirect('manual-signout')}
                className="text-sm text-text-muted hover:text-text-primary underline"
              >
                Sign out and start over
              </button>
            </div>
          </div>
        )}

        <span className="sr-only">Loading…</span>
      </div>
    </div>
  )
}
